import { AlertTriangle, CheckCircle2, Clock, Crosshair, Gavel, ListOrdered, Radar, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Backorder, BackorderStatus, Drop, DropStatus } from '../../../api/areas/market';
import { Callout, Section, StatCard, StatGrid } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { ProgressRow, Stepper, TaskList, TaskRow } from '../../../components/flow';
import { PageHeader } from '../../../components/PageHeader';
import { Loading } from '../../../components/States';
import { hubUrl } from '../../../nav/hubs';
import { RefreshButton } from '../../registry/shared';
import { BackorderLinks, counted, useBackorderRows } from '../module';
import { useNow } from '../shared';

const LIVE: BackorderStatus[] = ['ACTIVE', 'ASSIGNED', 'IN_AUCTION', 'WON', 'REGISTERING'];
const HOUR = 3_600_000;

function dropsWithin(drops: Drop[], hours: number, now: number): Drop[] {
  return drops.filter((d) => {
    const at = new Date(d.expected_drop_at).getTime();
    return (d.status === 'SCHEDULED' || d.status === 'ASSIGNED') && at >= now - HOUR && at <= now + hours * HOUR;
  });
}

/** `/backorders` (tab Tổng quan), reference 58: the day's backorder work at a glance. */
export function BackorderOverviewPage() {
  const { t } = useTranslation('market');
  const { backorders, drops, reload } = useBackorderRows();
  const now = useNow(60_000);
  const header = <PageHeader title={t('bo.overview.title')} subtitle={t('bo.overview.subtitle')} actions={<RefreshButton onClick={reload} />} />;
  if (backorders.status === 'loading' || drops.status === 'loading') {
    return (
      <>
        {header}
        <Loading rows={6} />
      </>
    );
  }
  if (backorders.status === 'error' || drops.status === 'error') {
    return (
      <>
        {header}
        <DataError error={backorders.status === 'error' ? backorders.error : drops.status === 'error' ? drops.error : null} onRetry={reload} title={t('bo.loadError')} />
      </>
    );
  }
  const bos: Backorder[] = backorders.data.items;
  const ds: Drop[] = drops.data.items;
  const by = (s: BackorderStatus) => bos.filter((b) => b.status === s).length;
  const dropBy = (s: DropStatus) => ds.filter((d) => d.status === s).length;
  const live = bos.filter((b) => LIVE.includes(b.status));
  const soon48 = dropsWithin(ds, 48, now);
  const soon24 = dropsWithin(ds, 24, now);
  const attempts = ds.map((d) => d.attempt).filter((a) => a !== null);
  const catching = attempts.filter((a) => a.status === 'PLANNED' || a.status === 'POLLING' || a.status === 'REGISTERING');
  const review = attempts.filter((a) => a.status === 'MANUAL_REVIEW');
  const won = bos.filter((b) => b.status === 'CAUGHT' || b.status === 'WON');
  const bc = (n: number) => counted(bos, n);
  const dc = (n: number) => counted(ds, n);
  const requests = (status?: string) => hubUrl('backorder', 'requests', undefined, status ? { status } : undefined);
  const dropsTo = (status?: string) => hubUrl('backorder', 'drops', undefined, status ? { status } : undefined);

  return (
    <>
      {header}
      <BackorderLinks here="overview" />
      <div className="space-y-5">
        <StatGrid>
          <StatCard label={t('bo.overview.open')} icon={ListOrdered} value={bc(live.length)} hint={t('bo.overview.openHint')} to={requests('ACTIVE')} linkLabel={t('bo.show')} />
          <StatCard label={t('bo.overview.soon')} icon={Clock} tone={soon48.length ? 'pending' : 'neutral'} value={dc(soon48.length)} hint={t('bo.overview.soonHint')} to={dropsTo()} linkLabel={t('bo.show')} />
          <StatCard label={t('bo.overview.catching')} icon={Crosshair} tone={catching.length ? 'info' : 'neutral'} value={dc(catching.length)} hint={t('bo.overview.catchingHint')} to={hubUrl('backorder', 'catching')} linkLabel={t('bo.show')} />
          <StatCard label={t('bo.overview.won')} icon={CheckCircle2} tone={won.length ? 'ok' : 'neutral'} value={bc(won.length)} hint={t('bo.overview.wonHint')} to={requests('CAUGHT')} linkLabel={t('bo.show')} />
        </StatGrid>

        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_380px] [&>*]:min-w-0">
          <Section title={t('bo.overview.pipeline')} description={t('bo.overview.pipelineHint')}>
            <div className="space-y-5">
              <Stepper
                label={t('bo.overview.pipeline')}
                steps={[
                  { id: 'discover', label: t('bo.stage.discover'), hint: t('bo.stage.discoverHint'), count: dc(dropBy('SCHEDULED')), state: 'done', to: dropsTo('SCHEDULED') },
                  { id: 'request', label: t('bo.stage.request'), hint: t('bo.stage.requestHint'), count: bc(by('ACTIVE')), state: 'current', to: requests('ACTIVE') },
                  { id: 'catch', label: t('bo.stage.catch'), hint: t('bo.stage.catchHint'), count: dc(catching.length), state: 'todo', to: hubUrl('backorder', 'catching') },
                  { id: 'allocate', label: t('bo.stage.allocate'), hint: t('bo.stage.allocateHint'), count: bc(by('ASSIGNED') + by('IN_AUCTION')), state: 'todo', to: requests('ASSIGNED') },
                ]}
              />
              <div className="space-y-4 border-t pt-4">
                <ProgressRow label={t('bo.overview.rowScheduled')} value={dropBy('SCHEDULED')} total={ds.length} tone="pending" to={dropsTo('SCHEDULED')} right={t('bo.overview.ofDrops', { n: dropBy('SCHEDULED'), total: dc(ds.length) })} />
                <ProgressRow label={t('bo.overview.rowAssigned')} value={dropBy('ASSIGNED')} total={ds.length} tone="info" to={dropsTo('ASSIGNED')} right={t('bo.overview.ofDrops', { n: dropBy('ASSIGNED'), total: dc(ds.length) })} />
                <ProgressRow label={t('bo.overview.rowAuction')} value={dropBy('AUCTION')} total={ds.length} tone="pending" to={dropsTo('AUCTION')} right={t('bo.overview.ofDrops', { n: dropBy('AUCTION'), total: dc(ds.length) })} />
                <ProgressRow label={t('bo.overview.rowCaught')} value={dropBy('CAUGHT')} total={ds.length} tone="ok" to={dropsTo('CAUGHT')} right={t('bo.overview.ofDrops', { n: dropBy('CAUGHT'), total: dc(ds.length) })} />
                <ProgressRow label={t('bo.overview.rowNotCaught')} value={dropBy('NOT_CAUGHT')} total={ds.length} tone="serious" to={dropsTo('NOT_CAUGHT')} right={t('bo.overview.ofDrops', { n: dropBy('NOT_CAUGHT'), total: dc(ds.length) })} />
              </div>
            </div>
          </Section>
          <Section title={t('bo.overview.tasks')}>
            <TaskList>
              <TaskRow icon={Radar} title={t('bo.task.soon')} hint={t('bo.task.soonHint')} count={dc(soon24.length)} tone={soon24.length ? 'warn' : 'neutral'} to={dropsTo()} />
              <TaskRow icon={AlertTriangle} title={t('bo.task.review')} hint={t('bo.task.reviewHint')} count={dc(review.length)} tone={review.length ? 'crit' : 'neutral'} to={hubUrl('backorder', 'catching')} />
              <TaskRow icon={Gavel} title={t('bo.task.auction')} hint={t('bo.task.auctionHint')} count={bc(by('IN_AUCTION'))} tone={by('IN_AUCTION') ? 'info' : 'neutral'} to={requests('IN_AUCTION')} />
              <TaskRow icon={Undo2} title={t('bo.task.notCaught')} hint={t('bo.task.notCaughtHint')} count={bc(by('NOT_CAUGHT'))} tone={by('NOT_CAUGHT') ? 'serious' : 'neutral'} to={requests('NOT_CAUGHT')} />
            </TaskList>
          </Section>
        </div>

        <Callout tone="warn" title={t('bo.noGuaranteeTitle')}>
          {t('bo.noGuarantee')}
        </Callout>
      </div>
    </>
  );
}
